/**
 * 올린 HTML 을 "적용할 화면" 에 맞춰 보는 계산(2026-10-20) — 순수 계산이라 시험한다.
 *
 * 만든 사람은 화면 크기를 모르고, 운영자는 그 HTML 이 **지금 등록된 저 화면**에서 어떻게 나갈지를 알고 싶다. 가로·세로·4:3
 * 같은 틀로는 답이 안 된다 — 사무실 스틱의 웹뷰는 962×541 이고(TV 1920×1080, 화면 배율), 윈도우 PC 는 1440×900 이다.
 * 그래서 고르는 목록은 **등록된 화면이 보고한 크기**로 만들고(`screenFormat.ts` 의 `formatOf` — 방향에 맞춰 세운 값), 그 크기로
 * 재생 화면과 같은 계산(`htmlFitBox`)을 해 "몇 %로 줄여 띄우고 어디가 비거나 잘리는지" 를 말로 적는다.
 */
import { HTML_SIZE_MAX, HTML_SIZE_MIN, htmlFitBox, type HtmlFit } from '@signageone/core';
import { formatOf } from './screenFormat.js';

export type Size = { width: number; height: number };

/** 고를 수 있는 화면 하나 — 등록된 화면이거나 흔한 틀. */
export interface TargetScreen {
  /** 고르는 칸의 값 — `display:<id>` 또는 `preset:<id>`. */
  key: string;
  /** 등록된 화면이면 그 id(적용할 화면으로 저장한다), 틀이면 null. */
  displayId: string | null;
  name: string;
  /** "962×541 · 가로 16:9" — 크기를 모르면 "화면 크기 보고 전". */
  detail: string;
  /** 크기를 모르면 null — 고를 수 없다(보고하기 전의 화면, 꺼져 있던 화면). */
  size: Size | null;
  /** 에이전트 판(화면이 보고한 그대로, "0.7.17 (shell 0.7.17)"). 틀이면 null. */
  appVersion: string | null;
}

/** 등록된 화면이 없거나 보고 전일 때 고르는 흔한 틀. */
export const PRESET_SCREENS: readonly TargetScreen[] = [
  { key: 'preset:fhd', displayId: null, name: '가로 FHD', detail: '1920×1080', size: { width: 1920, height: 1080 }, appVersion: null },
  { key: 'preset:hd', displayId: null, name: '가로 HD', detail: '1280×720', size: { width: 1280, height: 720 }, appVersion: null },
  { key: 'preset:portrait', displayId: null, name: '세로 FHD', detail: '1080×1920', size: { width: 1080, height: 1920 }, appVersion: null },
  { key: 'preset:xga', displayId: null, name: '4:3', detail: '1024×768', size: { width: 1024, height: 768 }, appVersion: null },
];

/** 목록에 쓰는 화면 모양 — `Display` 에서 쓰는 것만. */
export interface ScreenSource {
  id: string;
  name: string;
  orientation: 'landscape' | 'portrait';
  hidden?: boolean;
  status: { screen_w: number | null; screen_h: number | null; app_version?: string | null };
}

/**
 * 등록된 화면을 고르는 목록으로 — 이름 순. 감춘 화면(철거 예정·장기 수리)은 빼되, 이미 적용할 화면으로 정해 둔 것은 남긴다.
 */
export function displayScreens(displays: readonly ScreenSource[], keepId: string | null = null): TargetScreen[] {
  return displays
    .filter((display) => !display.hidden || display.id === keepId)
    .map((display) => {
      const format = formatOf(display);
      const size = format.width && format.height ? { width: format.width, height: format.height } : null;
      return {
        key: `display:${display.id}`,
        displayId: display.id,
        name: display.name || '(이름 없음)',
        detail: size ? format.label : '화면 크기 보고 전',
        size,
        appVersion: display.status.app_version ?? null,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
}

/**
 * 처음 보여 줄 화면 — 정해 둔 적용할 화면, 없으면 등록된 화면 가운데 가장 흔한 크기의 첫 화면, 그것도 없으면 가로 FHD.
 *
 * 흔한 크기를 고르는 까닭: 정하기 전에도 "우리 화면에서는 이렇게 나간다" 를 먼저 보여 준다 — 틀(FHD)부터 보여 주면 정작 그
 * 크기의 화면이 하나도 없는 곳에서 엉뚱한 그림을 믿게 된다.
 */
export function defaultTargetKey(screens: readonly TargetScreen[], savedDisplayId: string | null): string {
  const saved = savedDisplayId ? screens.find((screen) => screen.displayId === savedDisplayId && screen.size) : undefined;
  if (saved) return saved.key;
  const counts = new Map<string, { count: number; first: TargetScreen }>();
  for (const screen of screens) {
    if (!screen.size || !screen.displayId) continue;
    const id = `${screen.size.width}x${screen.size.height}`;
    const entry = counts.get(id);
    if (entry) entry.count += 1;
    else counts.set(id, { count: 1, first: screen });
  }
  let best: { count: number; first: TargetScreen } | null = null;
  for (const entry of counts.values()) if (!best || entry.count > best.count) best = entry;
  return best ? best.first.key : PRESET_SCREENS[0]!.key;
}

/** 고르는 칸의 값으로 화면을 찾는다(등록된 화면 → 틀). */
export function findScreen(screens: readonly TargetScreen[], key: string): TargetScreen | null {
  return screens.find((screen) => screen.key === key) ?? PRESET_SCREENS.find((screen) => screen.key === key) ?? null;
}

/** 지금 화면과 크기가 다른 등록된 화면들 — 크기마다 하나씩(몇 대인지와 함께), 많은 순으로 `limit` 개. */
export function otherScreenSizes(screens: readonly TargetScreen[], current: Size, limit = 3): Array<{ size: Size; names: string[] }> {
  const groups = new Map<string, { size: Size; names: string[] }>();
  for (const screen of screens) {
    if (!screen.size || !screen.displayId) continue;
    if (screen.size.width === current.width && screen.size.height === current.height) continue;
    const id = `${screen.size.width}x${screen.size.height}`;
    const group = groups.get(id) ?? { size: screen.size, names: [] };
    group.names.push(screen.name);
    groups.set(id, group);
  }
  return [...groups.values()].sort((a, b) => b.names.length - a.names.length).slice(0, limit);
}

/** "로비 1" · "로비 1 외 2대". */
export function namesLabel(names: readonly string[]): string {
  if (names.length === 0) return '';
  return names.length === 1 ? names[0]! : `${names[0]} 외 ${names.length - 1}대`;
}

/** 조정 중인 값 — 맞춤 방식과 기준 크기(null 이면 늘고 주는 HTML, 화면 크기 그대로). */
export interface FitDraft {
  fit: HtmlFit;
  size: Size | null;
}

/** 그 화면에서 어떻게 나가는가 — 한 줄과 판정(잘리는 곳이 있으면 `warn`). */
export interface FitReadout {
  text: string;
  tone: 'ok' | 'warn';
}

const px = (value: number): number => Math.round(value);

/**
 * 그 화면에서 어떻게 나가는가를 말로 — 재생 화면과 같은 계산(`htmlFitBox`).
 *
 * 숫자를 함께 적는다: "1366×768 → 70% 줄임(673×378) · 위아래 검은 띠 각 81px". 비율만 말하면 그 화면에서 글자가 읽힐
 * 크기인지 가늠할 수 없다. 1px 안쪽의 어긋남은 꼭 맞는 것으로 본다(반올림).
 *
 * `content` 는 HTML 이 실제로 그린 크기(콘솔이 잰 것 — 사람이 정한 뒤에는 모른다). 기준 크기를 그보다 작게 잡으면 기준 크기
 * 밖으로 나간 부분이 잘린다 — 미리보기에는 보이는데 "꼭 맞습니다" 라고 적으면 거짓이다. "그대로" 는 기준 크기를 쓰지 않고
 * 화면 크기에 그대로 그리므로 그린 크기와 화면을 견준다.
 */
export function fitReadout(screen: Size, draft: FitDraft, content: Size | null = null): FitReadout {
  const design = draft.size;
  if (!design) {
    return { text: `화면 크기 ${screen.width}×${screen.height} 그대로 · 늘고 주는 HTML 이라 화면을 채웁니다`, tone: 'ok' };
  }
  const head = `${design.width}×${design.height}`;
  if (draft.fit === 'none') {
    const shown = content ?? design;
    const parts: string[] = [];
    const dx = shown.width - screen.width;
    const dy = shown.height - screen.height;
    if (dx > 0) parts.push(`오른쪽 ${dx}px 잘림`);
    else if (dx < 0) parts.push(`오른쪽 ${-dx}px 빔`);
    if (dy > 0) parts.push(`아래 ${dy}px 잘림`);
    else if (dy < 0) parts.push(`아래 ${-dy}px 빔`);
    return {
      text: `${shown.width}×${shown.height} 그대로 · ${parts.length ? parts.join(' · ') : '화면에 꼭 맞습니다'}`,
      tone: dx > 0 || dy > 0 ? 'warn' : 'ok',
    };
  }
  const mode = draft.fit === 'cover' ? 'cover' : 'contain';
  const box = htmlFitBox(design, screen, mode);
  const percent = Math.round(box.scale * 100);
  const verb = percent < 100 ? '줄임' : percent > 100 ? '늘림' : '그대로';
  const shown = `${px(design.width * box.scale)}×${px(design.height * box.scale)}`;
  const left = px(box.left);
  const top = px(box.top);
  let edge: string;
  let tone: FitReadout['tone'] = 'ok';
  // 1px 은 반올림의 찌꺼기다 — 보이는 크기로 기준 크기를 정하면 가로·세로를 따로 반올림해 생긴다. 띠·잘림으로 치지 않는다.
  if (mode === 'contain') {
    edge = left > 1 ? `좌우 검은 띠 각 ${left}px` : top > 1 ? `위아래 검은 띠 각 ${top}px` : '화면에 꼭 맞습니다';
  } else {
    edge = left < -1 ? `좌우 각 ${-left}px 잘림` : top < -1 ? `위아래 각 ${-top}px 잘림` : '화면에 꼭 맞습니다';
    if (left < -1 || top < -1) tone = 'warn';
  }
  const over: string[] = [];
  if (content && content.width > design.width) over.push(`오른쪽 ${content.width - design.width}px`);
  if (content && content.height > design.height) over.push(`아래 ${content.height - design.height}px`);
  if (over.length > 0) {
    edge += ` · HTML 은 ${content!.width}×${content!.height} 로 그려 ${over.join('·')} 가 기준 크기 밖이라 잘립니다`;
    tone = 'warn';
  }
  return { text: `${head} → ${percent}% ${verb}(${shown}) · ${edge}`, tone };
}

/**
 * 이 화면의 에이전트가 화면 맞춤을 모르는가 — 0.7.18 부터 따른다. 모르면 기준 크기와 상관없이 화면 크기 그대로 띄운다.
 * 판을 모르면(보고 전·틀) 거짓 — 모르는 것을 경고하지 않는다.
 */
export function fitUnsupported(appVersion: string | null): boolean {
  const match = appVersion?.match(/(\d+)\.(\d+)\.(\d+)/);
  if (!match) return false;
  const [major, minor, patch] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (major !== 0) return major < 0;
  if (minor !== 7) return minor < 7;
  return patch < 18;
}

/** "보이는 크기" 로 고를 수 있는 범위(%). */
export const ZOOM_MIN = 25;
export const ZOOM_MAX = 300;

/**
 * 그 화면에서 보이는 배율(%) — 늘고 주는 HTML 은 100. "그대로" 는 기준 크기를 쓰지 않아 늘 100 이다(배율을 바꿀 수 없다 —
 * null).
 */
export function visiblePercent(screen: Size, draft: FitDraft): number | null {
  if (!draft.size) return 100;
  if (draft.fit === 'none') return null;
  return Math.round(htmlFitBox(draft.size, screen, draft.fit === 'cover' ? 'cover' : 'contain').scale * 100);
}

/**
 * "보이는 크기" 를 `percent` 로 맞추는 기준 크기 — 지금 기준 크기를 같은 비율로 늘이거나 줄인다.
 *
 * 배율은 화면 ÷ 기준 크기라 **기준 크기를 줄이면 크게 보인다** — 사람은 "크게·작게" 로 생각하지 기준 W×H 로 생각하지 않는다.
 * 늘고 주는 HTML 이면 화면 크기에서 시작한다(그 화면에서 100%). 기준 크기의 한도(64~7680) 안으로 자른다.
 */
export function zoomedSize(screen: Size, draft: FitDraft, percent: number): Size {
  const base = draft.size ?? screen;
  const mode = draft.fit === 'cover' ? 'cover' : 'contain';
  const current = draft.size ? htmlFitBox(base, screen, mode).scale : 1;
  const factor = current / (percent / 100);
  const clamp = (value: number): number => Math.min(HTML_SIZE_MAX, Math.max(HTML_SIZE_MIN, Math.round(value)));
  return { width: clamp(base.width * factor), height: clamp(base.height * factor) };
}

/**
 * 맞춤 방식을 바꿔도 이 화면에서 달라 보이지 않는 까닭 — 없으면 null.
 *
 * [고정 크기] 를 처음 누르면 기준 크기가 이 화면 크기로 잡혀, 맞춤·채움·그대로 어느 것을 눌러도 그림이 그대로였다 — 눌러 본
 * 사람은 "반응이 없다" 로 읽었다(2026-10-20). 같아 보이는 것이 맞다고, 무엇을 바꾸면 달라지는지를 말한다.
 */
export function sameLookNote(screen: Size, size: Size | null): string | null {
  if (!size) return null;
  if (size.width === screen.width && size.height === screen.height) {
    return '기준 크기가 이 화면과 같아 맞춤·채움·그대로가 똑같이 보입니다 — 보이는 크기나 기준 크기를 바꾸면 달라집니다';
  }
  const ratio = size.width / size.height / (screen.width / screen.height);
  if (Math.abs(ratio - 1) < 0.005) return '기준 크기의 비율이 이 화면과 같아 맞춤과 채움이 똑같이 보입니다';
  return null;
}

/**
 * HTML 이 실제로 배치되는 창 — 기준 크기가 있고 "그대로" 가 아니면 기준 크기, 아니면 화면 크기(재생 화면과 같다). 잘림은 이
 * 창에서 잰다.
 */
export function layoutViewport(screen: Size, draft: FitDraft): Size {
  return draft.size && draft.fit !== 'none' ? draft.size : screen;
}

/** 그 창이 화면에서 몇 배로 보이는가(잘린 양을 화면 픽셀로 옮길 때). 늘고 주는 HTML·"그대로" 는 1. */
export function layoutScale(screen: Size, draft: FitDraft): number {
  if (!draft.size || draft.fit === 'none') return 1;
  return htmlFitBox(draft.size, screen, draft.fit === 'cover' ? 'cover' : 'contain').scale;
}

/** 잰 값 가운데 잘림을 가르는 데 쓰는 것 — 창 크기와 끝까지 내려가 잰 내용 상자. */
export interface ContentMeasure {
  vw: number;
  vh: number;
  full?: { right: number; bottom: number } | null;
  /** 창 아래 끝에 딱 붙은 상자(100vh 틀·바닥 띠)를 뺀 내용의 끝(`HtmlMeasure.near`). */
  near?: number | null;
}

/**
 * 창 밖으로 나가 잘리는 양(창 픽셀) — 아래·오른쪽. 끝까지 잰 상자가 없으면(옛 스크립트·그릴 것 없음) null.
 * 1px 은 반올림 몫이라 잘림으로 치지 않는다.
 */
export function contentCut(measure: ContentMeasure): { bottom: number; right: number } | null {
  if (!measure.full) return null;
  const bottom = Math.ceil(measure.full.bottom - measure.vh);
  const right = Math.ceil(measure.full.right - measure.vw);
  return { bottom: bottom > 1 ? bottom : 0, right: right > 1 ? right : 0 };
}

/**
 * "화면에 다 맞추기" 의 합격선 — 잘리지 않고, 아래에 창 높이의 2%(최소 4px) 여유가 남는다.
 *
 * 여유를 두는 까닭: 콘솔은 운영자 PC 의 글꼴로 재고 화면은 기기 글꼴로 그린다. 사무실 스틱(안드로이드)에서 잰 내용 끝은 950px 이었고
 * 맥 웹킷과 몇 px 씩 달랐다 — 딱 맞춘 크기는 기기에서 한 줄이 잘릴 수 있다.
 */
export function fitsWhole(measure: ContentMeasure): boolean {
  if (!measure.full) return false;
  // 잘리면 탈락 — 창 끝에 딱 맞춰 끝나는 틀(100vh)·바닥 띠는 잘린 것이 아니다(1px 은 반올림 몫).
  if (measure.full.bottom > measure.vh + 1 || measure.full.right > measure.vw + 1) return false;
  // 여유는 내용이 실제로 끝나는 자리로 본다 — 틀까지 넣으면 어떤 크기에서도 여유가 없다(2026-10-20 운영 HTML 의 100vh 틀).
  const end = measure.near === undefined ? measure.full.bottom : (measure.near ?? 0);
  return end <= measure.vh - Math.max(4, measure.vh * 0.02);
}

/** "화면에 다 맞추기" 가 먼저 재 보는 크기(%) — 크게부터. 그다음 경계 사이를 5% 씩 다시 잰다(`fineZooms`). */
export const COARSE_ZOOMS: readonly number[] = [200, 150, 100, 75, 50, ZOOM_MIN];

/** 맞는 크기와 잘리는 크기 사이를 5% 씩 — 둘 다 빼고. */
export function fineZooms(fits: number, cuts: number): number[] {
  const out: number[] = [];
  for (let zoom = fits + 5; zoom < cuts; zoom += 5) out.push(zoom);
  return out;
}

/** 다 들어가는 가장 큰 크기(%) — 하나도 없으면 null. 잴 수 없었던 것(`fits` null)은 건너뛴다. */
export function bestZoom(results: ReadonlyArray<{ zoom: number; fits: boolean | null }>): number | null {
  let best: number | null = null;
  for (const result of results) if (result.fits && (best === null || result.zoom > best)) best = result.zoom;
  return best;
}

/** 두 조정 값이 같은가. */
export function sameDraft(a: FitDraft, b: FitDraft): boolean {
  return a.fit === b.fit && a.size?.width === b.size?.width && a.size?.height === b.size?.height;
}

/**
 * 저장할 것 — 바뀐 것만 싣는다. 기준 크기를 고쳤으면 사람이 정한 것으로 적는다(콘솔이 저절로 다시 재서 덮지 않게).
 * 바뀐 것이 없으면 빈 객체.
 */
export function fitPatch(
  saved: FitDraft & { targetDisplayId: string | null },
  draft: FitDraft & { targetDisplayId: string | null },
): {
  html_fit?: HtmlFit;
  html_size?: Size | null;
  html_size_manual?: boolean;
  html_target_display_id?: string | null;
} {
  const patch: ReturnType<typeof fitPatch> = {};
  if (draft.fit !== saved.fit) patch.html_fit = draft.fit;
  if (draft.size?.width !== saved.size?.width || draft.size?.height !== saved.size?.height) {
    patch.html_size = draft.size ? { ...draft.size } : null;
    patch.html_size_manual = true;
  }
  if (draft.targetDisplayId !== saved.targetDisplayId) patch.html_target_display_id = draft.targetDisplayId;
  return patch;
}
